/** CARTO raster tiles began requiring keys in September 2026.
 * A missing key returns watermarked images with HTTP 200, not a tileerror.
 * Never request CARTO without a key. */
export function hasCartoApiKey(): boolean {
  return Boolean(String(import.meta.env.VITE_CARTO_API_KEY ?? '').trim());
}

/** Add the public, domain-restricted CARTO basemap key to a tile URL. */
export function withCartoApiKey(url: string): string {
  const key = String(import.meta.env.VITE_CARTO_API_KEY ?? '').trim();
  if (!key) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}key=${encodeURIComponent(key)}`;
}


/**
 * Use CARTO's road-only and transparent labels-only Voyager tiles as separate
 * layers.  The label pane sits above section highlights and SVG door dots, but below
 * larger map marker icons, interactive tooltips, and popups. It never intercepts map taps.
 *
 * Keep `detectRetina` disabled: the {r} URL token requests @2x tiles on retina
 * devices without Leaflet changing the tile zoom level (which would shrink
 * street-name typography by half). CARTO itself controls label density by zoom.
 */
export function addReadableStreetBasemap(map: any, leaflet: any): void {
  // Normal viewport-only OSM tiles are the safe no-key fallback.  We prefer
  // CARTO's separate road and labels panes when a free key is configured.
  // OSM tile.openstreetmap.org requires visible attribution and does not permit
  // offline/prefetch/bulk downloads.
  if (!hasCartoApiKey()) {
    leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 20,
      maxNativeZoom: 19,
      detectRetina: false,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
    }).addTo(map);
    return;
  }
  const paneName = 'coverageStreetLabels';
  const pane = map.getPane(paneName) ?? map.createPane(paneName);
  pane.style.zIndex = '450'; // vectors=400, markers=600, tooltips=650, popups=700
  pane.style.pointerEvents = 'none';
  const options = {
    maxZoom: 20,
    subdomains: 'abcd',
    detectRetina: false
  };
  const roadTiles = leaflet.tileLayer(
    withCartoApiKey('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png'),
    { ...options, attribution: '&copy; OpenStreetMap contributors &copy; CARTO' }
  ).addTo(map);
  const streetLabels = leaflet.tileLayer(
    withCartoApiKey('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png'),
    { ...options, pane: paneName, interactive: false }
  ).addTo(map);

  let degraded = false;
  let roadErrors = 0;
  let labelErrors = 0;
  const fallback = (): void => {
    if (degraded) return;
    degraded = true;
    map.removeLayer(roadTiles);
    map.removeLayer(streetLabels);
    // Avoid the CARTO watermarked fallback entirely: error/blocked tiles
    // switch to OpenStreetMap's normal labeled basemap instead.
    leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 20,
      maxNativeZoom: 19,
      detectRetina: false,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
    }).addTo(map);
  };
  roadTiles.on('tileerror', () => { if (++roadErrors >= 4) fallback(); });
  streetLabels.on('tileerror', () => { if (++labelErrors >= 4) fallback(); });
}
